// ABOUTME: API client for the Tradespurple Worker backend.
// ABOUTME: Uses Bearer tokens stored in localStorage for auth.

// Resolve the API base robustly. An explicit VITE_API_URL always wins (manual
// builds set it). Otherwise fall back by hostname so a build that *forgot* to set
// the env var — e.g. Cloudflare Pages' git-integrated build — still points at the
// real Worker in production instead of dev's localhost. Only true localhost dev
// uses 127.0.0.1:8787.
const PROD_API_URL = 'https://tradespurple-api.maxtattonbrown.workers.dev';
const isLocalhost =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
const API_URL =
  import.meta.env.VITE_API_URL || (isLocalhost ? 'http://localhost:8787' : PROD_API_URL);
const TOKEN_KEY = 'tp_bearer';

// Paths where a 401 is an expected (non-expiry) response — don't auto-logout.
const AUTH_EXEMPT_PATHS = [
  '/auth/session',
  '/auth/signin',
  '/auth/signup',
  '/auth/reset-password',
  '/auth/request-password-reset',
];

type ApiOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
};

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function apiFetch<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = opts;
  const authHeaders: Record<string, string> = {};
  const token = getToken();
  if (token) authHeaders['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    if (res.status === 401 && !AUTH_EXEMPT_PATHS.some(p => path.startsWith(p))) {
      // Session has expired — clear stored token and notify the app
      setToken(null);
      window.dispatchEvent(new CustomEvent('tp:auth-expired'));
      throw new Error('Session expired');
    }
    const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
    throw new Error(err.error || `API error ${res.status}`);
  }

  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  setToken,
  getToken,
};
